<script setup lang="ts">
import type { StrapiLocale } from '@nuxtjs/strapi/dist/runtime/types'
import { UiEventComponent } from '@ui'
import type { StrapiBlog } from '~~/types/strapi'
import type { EventEntry } from '~~/types/types'

const {
  public: { appLink },
} = useRuntimeConfig()

definePageMeta({
  key: (route) => route.fullPath,
  layout: 'base',
})

const route = useRoute()
const dayjs = useDayjs()
const { locale } = useI18n()
const { find } = useStrapi()
const localeRoute = useLocaleRoute()

const slug = computed(() => route.params.blog_slug as string)

const queryData = computed(() => ({
  locale: locale.value as StrapiLocale,
  slug: slug.value,
}))

const { data: _pageData } = await useAsyncData(
  `blog:${locale.value}:${slug.value}`,
  () =>
    find<StrapiBlog>('blogs', {
      locale: queryData.value.locale,
      filters: {
        slug: {
          $eq: queryData.value.slug,
        },
      },
      pagination: {
        page: 0,
        pageSize: 1,
      },
      populate: {
        image: {
          populate: '*',
        },
        seo: {
          populate: '*',
        },
      },
    }),
  {
    watch: [queryData],
    immediate: true,
  }
)
const pageData = computed(() => _pageData.value?.data?.[0])

if (!pageData.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

const {
  public: { siteUrl },
} = useRuntimeConfig()

/**
 * SEO
 * TODO: refactor
 */
const title = computed(
  () => pageData.value?.seo?.metaTitle || pageData.value?.title || ''
)
const description = computed(
  () =>
    pageData.value?.seo?.metaDescription || pageData.value?.description || ''
)
const image = computed(
  () => pageData.value?.seo?.metaImage?.url || pageData.value?.image.url || ''
)

useSeoMeta({
  title: title,
  description: description,
  ogTitle: title,
  ogDescription: description,
  ogImage: image,
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogType: 'website',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: image,
  twitterCard: 'summary_large_image',
})

const relatedEventsData = ref()
const takeCount = ref(100)

if (slug.value === 'scavenger-hunt-bkk-24') {
  const hubId = '670582371a118ca18749afc2'
  const { data } = await useNuxtApp().$client.hubs.getActivitiesByHub.useQuery({
    hubId,
    take: takeCount.value,
  })

  relatedEventsData.value = data.value
}

const relatedEvents = computed(() => {
  const eventsByDate = relatedEventsData.value ?? {}
  return Object.values(eventsByDate).flat() as EventEntry[]
})

function isUrlSimple(str: string): boolean {
  return str.startsWith('http://') || str.startsWith('https://')
}

const Developers = [
  'Near x Protocol Labs Open Co-Working Day',
  'dAGI hack: Web3 х AI online hackathon',
  'Hackers Connect🛠-For Devcon&ETHGlobal Builders',
  'TON Ecosystem Builders Summit',
  'Hack Seasons Conference Bangkok',
  'Sui Hacker House',
  'TON Hacker House Bangkok',
  'Starknet Hacker House',
  'Sui Connect: Bangkok',
  'TON Bangkok Bootcamp | Hackers League Hackathon ',
  'DevRel Brunch',
  'Cosmos Hub @ DevCon',
  'Builder Nights Bangkok - Presented by MetaMask 🦊 , Linea, Safe, 1inch and Web3auth',
]

const Researchers = [
  'DYOR Powerhouse: Crypto Data and Research Night',
  'De.Fi World 2024 - Join BNB Chain, Linea, Manta, 1inch…',
  'Governance Day 3rd Edition - Devcon',
  'Proof Summit @ Devcon',
  'DAO Asia Summit',
  'Parallel Society Congress: Bangkok',
  'Ethereum Cypherpunk Congress by W3PN @ Devcon 7',
  'AVS Day Bangkok by Consensys, EigenLayer & AltLayer',
  'Whitepaper Reading at ZKHub',
  'Superintelligence Summit',
  'H.E.R. CON Bangkok',
]

const Investors = [
  'Venture Capital Community StartUp Pitch Competition',
  'Startup Day + VC Speed Dating | Supermoon & ICP @ Devcon',
  `Liquify Breakfast n' Shmooze w/ Supermoon & Cointelegraph Accelerator`,
  'Devcon 2024 | Meet the VC Brekky',
  'Meet the VC',
  'Up in the Cloud: DePIN Happy Hour',
  `Decentralized SEA: Building DePIN's Future In Southeast Asia`,
  'RWA Leaders Brunch: Devcon 2024 Edition',
  'BNB Incubation Alliance - Bangkok',
  'AVS Day Bangkok by Consensys, EigenLayer & AltLayer',
  `Hack n' Hang Devcon Edition `,
  'Hack Seasons Conference Bangkok',
  'Cosmos Hub @ DevCon',
  'Solana Siam - Unlocking New Frontiers with Solana',
  'DePIN Day Bangkok',
  `DEFI'ING BANGKOK`,
  'Pantera House - DevCon 2024',
  'DevCon Skyline Soirée',
  'TON Hacker House Bangkok',
  'DeFi Renaissance Day',
  'De.Fi World 2024 - Join BNB Chain, Linea, Manta, 1inch…',
]

const Founders = [
  'Venture Capital Community StartUp Pitch Competition',
  'Startup Day + VC Speed Dating | Supermoon & ICP @ Devcon',
  'Whitepaper Reading at ZKHub',
  'H.E.R. CON Bangkok',
  'Superintelligence Summit',
  'Web3 Social x AI Immersion',
  'CryptoMondays Bangkok (Devcon)',
  'EthScale @ Devcon 2024',
  'Find your co-founder at Startup Spark!',
  'De.Fi World 2024 - Join BNB Chain, Linea, Manta, 1inch…',
  'Devcon 2024 | Meet the VC Brekky',
  'Cosmos Hub @ DevCon',
  'Paradigm Cafe @ Devcon',
  'The Bull Rally: Go-Kart @ Devcon',
  'RWA Leaders Brunch: Devcon 2024 Edition',
  'Meet the VC',
  `Decentralized SEA: Building DePIN's Future In Southeast Asia`,
  'Web3 Night Bazzar',
  'Consumer Crypto Open Mic @Devcon',
  'DevCon Happy Hour with StarkWare, Pantera Capital & Braavos ',
  'BNB Incubation Alliance - Bangkok',
  `Hack n' Hang Devcon Edition`,
  'DeFi Renaissance Day',
  'Penguins @ Devcon',
  'WEB3 Elephant Beat Best',
  'KOL Gathering: Bangkok Edition',
  'TON Hacker House Bangkok',
  'Hack Seasons Conference Bangkok',
  'AVS Day Bangkok by Consensys, EigenLayer & AltLayer',
  'DevCon Skyline Soirée',
  'Up in the Cloud: DePIN Happy Hour',
  'Solana Siam - Unlocking New Frontiers with Solana',
  'DePIN Day Bangkok',
  `DEFI'ING BANGKOK`,
  `Devcon Founders' Brunch with Caladan x Magna`,
  'Linea x Bitkub: UNBOUND',
  'WOW SUMMIT BANGKOK 2024',
]

const Party = [
  'LabWeek Opening Party',
  'Web3 COSPLAY Night with CryptoJava',
  'sub0 Opening Party: A tuktuk disco experience',
  'chadGPT.wtf Rooftop Party',
  'OLYMPUS Founders Brunch',
  'Welcome To Bangkok',
  'Meme Mixer by Nest Wallet @ Devcon',
  'Zo x Farcaster Launch Party | 10th November',
  'ALL TIME HIGH // rooftop party',
  'Chain Fusion Tribe Night',
  'Azuki x Arbitrum - Devcon 2024',
  'sub0 closing party with OpenGuild',
  'NOVEMBULL Party - Blum, Trust Wallet, Impossible Finance, DOGS, Flipster',
  'CypheRave',
  'Web3 Social x AI Immersion',
  'Convergence: Networking with TOP Builders in SEA',
  '🍸MoveTails: Movement Cocktail Rooftop Chill',
  `Rooftop Drinks & Hors d'oeuvres with Wormhole `,
  'Open #AI Launch Party - RSS3',
  'NOVEMBULL Party - Blum, Trust Wallet, Impossible Finance, DOGS',
  'Serotonin Full Moon Festival',
  'DeAI : The After Party',
  'Metis Fest Bangkok',
  'Binance TH presents CryptoZonic Festival',
  'Blockchain Week & MemeWonder',
  'Boys Night Out Bangkok w/ Space and Time & Harpie',
  'Agents Unleashed Bangkok | Devcon 2024',
  'BIO/ACC RAVE BANGKOK',
  'The Best Event. DevCon BKK with TBV & BitcoinOS',
  'WEB3 Elephant Beat Best',
  'rAIve the Night in Bangkok',
  'Zircuit x API3 Present Zircuit Tribe | Devcon 2024',
  'Degen Night at MUIN',
  'KOL Gathering: Bangkok Edition',
  'Ready Player Party ?',
]

const filteredDevelopersEvents = relatedEvents.value.filter((event) =>
  Developers.includes(event.name)
)

const filteredResearchersEvents = relatedEvents.value.filter((event) =>
  Researchers.includes(event.name)
)

const filteredInvestorsEvents = relatedEvents.value.filter((event) =>
  Investors.includes(event.name)
)

const filteredFoundersEvents = relatedEvents.value.filter((event) =>
  Founders.includes(event.name)
)

const filteredPartyEvents = relatedEvents.value.filter((event) =>
  Party.includes(event.name)
)
</script>

<template>
  <div v-if="pageData">
    <div class="prose mx-auto max-w-255.75">
      <img
        v-if="pageData.image"
        :alt="pageData.title"
        :src="pageData.image.url"
        class="w-full h-91.75 object-cover <md:h-45"
      />
      <div class="content-blog">
        <div class="flex flex-row items-center mt-15.5">
          <img
            alt="Publication date"
            src="~/assets/images/nft-tickets/event-date.svg"
            class="mr-1.5 w-4 h-4"
          />

          <UiDateTime v-if="pageData.date" :value="pageData.date" />
          <UiDateTime v-else :value="pageData.publishedAt" />
        </div>

        <V-HTML :html="pageData!.content"></V-HTML>

        <template v-if="slug === 'scavenger-hunt-bkk-24'">
          <div v-if="relatedEvents.length">
            <div class="flex flex-col gap-3.5 max-w-255.75">
              <template v-for="event in filteredPartyEvents" :key="event.id">
                <NuxtLink
                  :to="localeRoute(`/events/${event.id}`)"
                  class="decoration-none"
                >
                  <UiEventComponent
                    :cover-image="
                      isUrlSimple(event.coverImage)
                        ? event.coverImage
                        : `${appLink}/up/activities/${event.id}_${event.coverImage}.jpg`
                    "
                    :variant="'with-block'"
                    class="bg-[#222]"
                  >
                    <template #location>
                      <div
                        v-if="event.online"
                        class="flex flex-row gap-2 w-full"
                      >
                        <img
                          src="/images/external/other/event-online.svg"
                          class="min-w-4 w-4 h-4"
                        />
                        <p class="text-white text-xs font-500 truncate m-0">
                          Online
                        </p>
                      </div>

                      <div
                        v-else
                        class="flex flex-row items-center gap-2 w-full"
                      >
                        <img
                          src="/images/external/other/icon-location-violet.svg"
                          class="min-w-2.5 w-2.5 h-2.5"
                        />
                        <p class="text-white text-xs font-500 truncate w-full">
                          {{ event.place.name }}
                        </p>
                      </div>
                    </template>
                    <template #title>
                      {{ event.name }}
                    </template>
                    <template #description>
                      {{ event.description }}
                    </template>
                    <template #date>
                      <UiDateTime :value="event.start" format="MMM D YYYY" />
                    </template>
                    <template #time>
                      <UiDateTime :value="event.start" format="LT" />
                    </template>
                  </UiEventComponent>
                </NuxtLink>
              </template>
            </div>

            <h2>2. Developers: Building the Future</h2>
            <p>
              DevCon is a goldmine for developers looking to collaborate, learn,
              and push boundaries. Our top sessions have been carefully chosen
              to keep you ahead of the curve in all things Web3 and blockchain
              tech.
            </p>

            <div class="flex flex-col gap-3.5 max-w-255.75">
              <template
                v-for="event in filteredDevelopersEvents"
                :key="event.id"
              >
                <NuxtLink
                  :to="localeRoute(`/events/${event.id}`)"
                  class="decoration-none"
                >
                  <UiEventComponent
                    :cover-image="
                      isUrlSimple(event.coverImage)
                        ? event.coverImage
                        : `${appLink}/up/activities/${event.id}_${event.coverImage}.jpg`
                    "
                    :variant="'with-block'"
                    class="bg-[#222]"
                  >
                    <template #location>
                      <div
                        v-if="event.online"
                        class="flex flex-row gap-2 w-full"
                      >
                        <img
                          src="/images/external/other/event-online.svg"
                          class="min-w-4 w-4 h-4"
                        />
                        <p class="text-white text-xs font-500 truncate m-0">
                          Online
                        </p>
                      </div>

                      <div
                        v-else
                        class="flex flex-row items-center gap-2 w-full"
                      >
                        <img
                          src="/images/external/other/icon-location-violet.svg"
                          class="min-w-2.5 w-2.5 h-2.5"
                        />
                        <p class="text-white text-xs font-500 truncate w-full">
                          {{ event.place.name }}
                        </p>
                      </div>
                    </template>
                    <template #title>
                      {{ event.name }}
                    </template>
                    <template #description>
                      {{ event.description }}
                    </template>
                    <template #date>
                      <UiDateTime :value="event.start" format="MMM D YYYY" />
                    </template>
                    <template #time>
                      <UiDateTime :value="event.start" format="LT" />
                    </template>
                  </UiEventComponent>
                </NuxtLink>
              </template>
            </div>

            <h2>3. Investors: Opportunities for Growth</h2>
            <p>
              If you’re on the hunt for the next big thing, these picks are for
              you. We’ve selected the top events for meeting visionary founders,
              exploring market trends, and finding game-changing projects.
            </p>

            <div class="flex flex-col gap-3.5 max-w-255.75">
              <template
                v-for="event in filteredInvestorsEvents"
                :key="event.id"
              >
                <NuxtLink
                  :to="localeRoute(`/events/${event.id}`)"
                  class="decoration-none"
                >
                  <UiEventComponent
                    :cover-image="
                      isUrlSimple(event.coverImage)
                        ? event.coverImage
                        : `${appLink}/up/activities/${event.id}_${event.coverImage}.jpg`
                    "
                    :variant="'with-block'"
                    class="bg-[#222]"
                  >
                    <template #location>
                      <div
                        v-if="event.online"
                        class="flex flex-row gap-2 w-full"
                      >
                        <img
                          src="/images/external/other/event-online.svg"
                          class="min-w-4 w-4 h-4"
                        />
                        <p class="text-white text-xs font-500 truncate m-0">
                          Online
                        </p>
                      </div>

                      <div
                        v-else
                        class="flex flex-row items-center gap-2 w-full"
                      >
                        <img
                          src="/images/external/other/icon-location-violet.svg"
                          class="min-w-2.5 w-2.5 h-2.5"
                        />
                        <p class="text-white text-xs font-500 truncate w-full">
                          {{ event.place.name }}
                        </p>
                      </div>
                    </template>
                    <template #title>
                      {{ event.name }}
                    </template>
                    <template #description>
                      {{ event.description }}
                    </template>
                    <template #date>
                      <UiDateTime :value="event.start" format="MMM D YYYY" />
                    </template>
                    <template #time>
                      <UiDateTime :value="event.start" format="LT" />
                    </template>
                  </UiEventComponent>
                </NuxtLink>
              </template>
            </div>

            <h2>4. Researchers: Delve Deeper into Innovation</h2>
            <p>
              For those who love diving deep into tech’s cutting edge, these
              sessions offer thought-provoking discussions, workshops, and
              insights that’ll leave you inspired and ready to shape the future.
            </p>

            <div class="flex flex-col gap-3.5 max-w-255.75">
              <template
                v-for="event in filteredResearchersEvents"
                :key="event.id"
              >
                <NuxtLink
                  :to="localeRoute(`/events/${event.id}`)"
                  class="decoration-none"
                >
                  <UiEventComponent
                    :cover-image="
                      isUrlSimple(event.coverImage)
                        ? event.coverImage
                        : `${appLink}/up/activities/${event.id}_${event.coverImage}.jpg`
                    "
                    :variant="'with-block'"
                    class="bg-[#222]"
                  >
                    <template #location>
                      <div
                        v-if="event.online"
                        class="flex flex-row gap-2 w-full"
                      >
                        <img
                          src="/images/external/other/event-online.svg"
                          class="min-w-4 w-4 h-4"
                        />
                        <p class="text-white text-xs font-500 truncate m-0">
                          Online
                        </p>
                      </div>

                      <div
                        v-else
                        class="flex flex-row items-center gap-2 w-full"
                      >
                        <img
                          src="/images/external/other/icon-location-violet.svg"
                          class="min-w-2.5 w-2.5 h-2.5"
                        />
                        <p class="text-white text-xs font-500 truncate w-full">
                          {{ event.place.name }}
                        </p>
                      </div>
                    </template>
                    <template #title>
                      {{ event.name }}
                    </template>
                    <template #description>
                      {{ event.description }}
                    </template>
                    <template #date>
                      <UiDateTime :value="event.start" format="MMM D YYYY" />
                    </template>
                    <template #time>
                      <UiDateTime :value="event.start" format="LT" />
                    </template>
                  </UiEventComponent>
                </NuxtLink>
              </template>
            </div>
            <h2>5. Founders: Elevate Your Vision</h2>
            <p>
              Whether you’re pitching your startup, seeking partners, or just
              soaking up knowledge, our picks will connect you with industry
              leaders, investors, and fellow entrepreneurs.
            </p>

            <div class="flex flex-col gap-3.5 max-w-255.75">
              <template v-for="event in filteredFoundersEvents" :key="event.id">
                <NuxtLink
                  :to="localeRoute(`/events/${event.id}`)"
                  class="decoration-none"
                >
                  <UiEventComponent
                    :cover-image="
                      isUrlSimple(event.coverImage)
                        ? event.coverImage
                        : `${appLink}/up/activities/${event.id}_${event.coverImage}.jpg`
                    "
                    :variant="'with-block'"
                    class="bg-[#222]"
                  >
                    <template #location>
                      <div
                        v-if="event.online"
                        class="flex flex-row gap-2 w-full"
                      >
                        <img
                          src="/images/external/other/event-online.svg"
                          class="min-w-4 w-4 h-4"
                        />
                        <p class="text-white text-xs font-500 truncate m-0">
                          Online
                        </p>
                      </div>

                      <div
                        v-else
                        class="flex flex-row items-center gap-2 w-full"
                      >
                        <img
                          src="/images/external/other/icon-location-violet.svg"
                          class="min-w-2.5 w-2.5 h-2.5"
                        />
                        <p class="text-white text-xs font-500 truncate w-full">
                          {{ event.place.name }}
                        </p>
                      </div>
                    </template>
                    <template #title>
                      {{ event.name }}
                    </template>
                    <template #description>
                      {{ event.description }}
                    </template>
                    <template #date>
                      <UiDateTime :value="event.start" format="MMM D YYYY" />
                    </template>
                    <template #time>
                      <UiDateTime :value="event.start" format="LT" />
                    </template>
                  </UiEventComponent>
                </NuxtLink>
              </template>
            </div>
          </div>

          <h2>Don’t Miss the Belong Scavenger Hunt!</h2>
          <p>
            While you’re exploring the best of DevCon, join in the fun with the
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://belong.net/hubs/bangkok-tech-and-crypto-week-scavenger-hunt-2024"
              >Belong Gasless NFT Scavenger Hunt</a
            >! To make things easier, you'll need to have the mobile app
            installed. It'll give you much better access and make everything
            more seamless!<br />Scan QR codes at select events to collect
            exclusive NFTs and win awesome prizes.
          </p>

          <p>
            Make your DevCon experience unforgettable with Belong’s curated
            picks. Dive into networking, learning, and fun—it’s time to make
            some amazing connections and memories!
          </p>
        </template>
      </div>
    </div>

    <SocialShareBar
      :title="pageData.title"
      :description="pageData.description"
    />

    <!-- <BlogLinks :pages="pagesData?.data" /> -->
  </div>
</template>

<style scoped>
@media (max-width: 1100px) {
  .content-blog {
    @apply px-3;
  }
}
</style>
